import { useCallback, useEffect, useMemo, useState } from "react";
import { produce } from "immer";

import { getText } from "shared/lib";
import {
  newRemovingTreeDescriptor,
  TreeModuleEventTypes,
  type NewRemovingTreeDescriptorExtraOptions,
  type TreeDescriptor,
} from "./lib";

import { TreeList, type TreeListProps } from "./treeList";
import { RemovingTreeDialog } from "./RemovingTreeDialog";

export interface RemovingTreeListProps {
  isOtherTreeActivity?: boolean;
  newTreeOptions?: NewRemovingTreeDescriptorExtraOptions;
  onChangeTreeDescriptors?: (treeDescriptors: Array<TreeDescriptor>) => void;
  onTreeDeleted?: (treeDescriptor: TreeDescriptor) => void;
  subTitle?: TreeListProps["subTitle"];
  title: TreeListProps["title"];
  treeDescriptors: Array<TreeDescriptor>;
  treesHaveBeenDeleted: boolean;
}

export function RemovingTreeList({
  isOtherTreeActivity,
  newTreeOptions,
  onChangeTreeDescriptors,
  onTreeDeleted,
  subTitle,
  title,
  treeDescriptors,
  treesHaveBeenDeleted,
}: RemovingTreeListProps) {
  const [treeDialogOpen, setTreeDialogOpen] = useState(false);

  const [selectedTreeDescriptorId, setSelectedTreeDescriptorId] = useState<
    string | undefined
  >();

  const selectedTreeDescriptor = useMemo(() => {
    return treeDescriptors.find((td) => td.treeId === selectedTreeDescriptorId);
  }, [selectedTreeDescriptorId, treeDescriptors]);

  const handleClickAdd = useCallback(() => {
    const lastTreeDescriptor = treeDescriptors.at(-1);
    let count = 0;
    if (lastTreeDescriptor) {
      count = parseInt(lastTreeDescriptor.title.split(" ").at(-1) || "0");
    }
    const newTreeDescriptor = newRemovingTreeDescriptor(
      {
        title: `${count + 1}`,
        getText,
      },
      newTreeOptions,
    );
    const newTreeDescriptors = treeDescriptors.concat(newTreeDescriptor);
    onChangeTreeDescriptors?.(newTreeDescriptors);
    setSelectedTreeDescriptorId(newTreeDescriptor.treeId);
    setTreeDialogOpen(true);
  }, [treeDescriptors]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickDelete = (treeDescriptor: TreeDescriptor) => {
    onChangeTreeDescriptors?.(
      treeDescriptors.filter((td) => td.treeId !== treeDescriptor?.treeId),
    );
    onTreeDeleted?.(treeDescriptor);
  };

  const handleClickDuplicate = (treeDescriptor: TreeDescriptor) => {
    if (!treeDescriptor) return;

    const lastTreeDescriptor = treeDescriptors.at(-1);
    let count = 0;
    if (lastTreeDescriptor) {
      count = parseInt(lastTreeDescriptor.title.split(" ").at(-1) || "0");
    }

    onChangeTreeDescriptors?.(
      produce(treeDescriptors, (draft) => {
        draft.push({
          ...treeDescriptor,
          title: `${count + 1}`,
          treeId: crypto.randomUUID(),
        });
      }),
    );
  };

  useEffect(() => {
    document.addEventListener(
      TreeModuleEventTypes.AddRemovingTree,
      handleClickAdd,
    );

    return () => {
      document.removeEventListener(
        TreeModuleEventTypes.AddRemovingTree,
        handleClickAdd,
      );
    };
  }, [handleClickAdd]);

  return (
    <>
      <TreeList
        emptyText={getText("You aren't proposing to remove any trees.")}
        isOtherTreeActivity={isOtherTreeActivity}
        onClick={(td) => {
          setSelectedTreeDescriptorId(td.treeId);
          setTreeDialogOpen(true);
        }}
        onClickAddTree={handleClickAdd}
        onClickDelete={handleClickDelete}
        onClickDuplicate={handleClickDuplicate}
        subTitle={subTitle}
        title={title}
        treeDescriptors={treeDescriptors}
        treesHaveBeenDeleted={treesHaveBeenDeleted}
      />

      <RemovingTreeDialog
        open={treeDialogOpen && !!selectedTreeDescriptor}
        onClickDelete={handleClickDelete}
        onClickNext={(td) => setSelectedTreeDescriptorId(td.treeId)}
        onClickPrev={(td) => setSelectedTreeDescriptorId(td.treeId)}
        onClickSubmit={() => setTreeDialogOpen(false)}
        onChangeTreeDescriptors={onChangeTreeDescriptors}
        onOpenChange={(open) => !open && setTreeDialogOpen(false)}
        treeDescriptor={selectedTreeDescriptor}
        treeDescriptors={treeDescriptors}
      />
    </>
  );
}
